import _ from 'lodash';
/** import third-party libraries in the section above, Sort Lines Ascending */

import { getDraftValidators_forPayload } from './validation-rules';
import { payload_formData } from './default-values';
import validate from 'utils/validate';
/** import application imports in the section above, Sort Lines Ascending */

/**
 * Canonical Ad Preset property display names keyed by `property.key` (ad form titles).
 * Overlay stored Mongo names with these at render / label resolution — no DB migration.
 */
export const AdPresetPropertyDisplayNames = {
    geotargets: 'Geotargeting',
    geoboxes: 'Geofencing',
    target_device_os: 'Device OS',
    target_carriers: 'Carrier / ISP',
    target_device_language: 'User Language',
    target_genders: 'Gender',
    target_age_groups: 'Age',
    target_dooh_publishers: 'DOOH Publishers',
    max_cpm_rate_local: 'Total Bid Price',
};

/**
 * Canonical group headers for checklist / summary (aliases stale Geo / Audience / Budget names).
 */
export const AdPresetGroupDisplayNames = {
    Targeting: 'Targeting',
    Inventory: 'Inventory',
    'Bid Optimization': 'Bid Optimization',
    Geo: 'Targeting',
    Audience: 'Targeting',
    'Budget & Optimization': 'Bid Optimization',
};

/**
 * Resolve a property display name by key, falling back to the stored name when unknown.
 *
 * @param {string} key
 * @param {string} [fallbackName]
 * @returns {string}
 */
export function getAdPresetPropertyDisplayName(key, fallbackName) {
    return AdPresetPropertyDisplayNames[key] ?? fallbackName ?? key;
}

/**
 * Resolve a group display name via alias map, falling back to the stored name.
 *
 * @param {string} groupName
 * @returns {string}
 */
export function getAdPresetGroupDisplayName(groupName) {
    return AdPresetGroupDisplayNames[groupName] ?? groupName;
}

/**
 * Overlay canonical display names onto an `ad_preset_properties` tree (display copy only).
 *
 * @param {Array<{ groupName: string, properties?: Array<{ key: string, name?: string }> }>} adPresetProperties
 * @returns {Array<{ groupName: string, properties: Array }> }
 */
export function applyAdPresetPropertyDisplayNames(adPresetProperties) {
    if (!Array.isArray(adPresetProperties)) {
        return [];
    }

    return adPresetProperties.map(group => ({
        ...group,
        groupName: getAdPresetGroupDisplayName(group.groupName),
        properties: (group.properties || []).map(property => ({
            ...property,
            name: getAdPresetPropertyDisplayName(property.key, property.name),
        })),
    }));
}

/**
 * Default ad-preset form payload and property checklist.
 * DOOH publisher fields default to empty arrays; new/edit form UI hides DOOH when
 * `canUseCTVOrDOOH()` is false (do not call getEnvironmentSettings here — circular with store).
 *
 * @returns {Object}
 */
export function getDefaults() {
    const defaults = { ...payload_formData };
    defaults.ad_preset_properties = [
        {
            groupName: getAdPresetGroupDisplayName('Targeting'),
            properties: [
                {
                    name: AdPresetPropertyDisplayNames.geotargets,
                    key: 'geotargets',
                    fields: ['geotargets'],
                    checked: false,
                },
                {
                    name: AdPresetPropertyDisplayNames.target_device_os,
                    key: 'target_device_os',
                    fields: ['target_device_os'],
                    checked: false,
                },
                {
                    name: AdPresetPropertyDisplayNames.target_carriers,
                    key: 'target_carriers',
                    fields: ['target_carriers', 'carriers_exclude'],
                    checked: false,
                },
                {
                    name: AdPresetPropertyDisplayNames.target_device_language,
                    key: 'target_device_language',
                    fields: ['target_device_language', 'target_device_language_exclude'],
                    checked: false,
                },
                {
                    name: AdPresetPropertyDisplayNames.target_genders,
                    key: 'target_genders',
                    fields: ['target_genders'],
                    checked: false,
                },
                {
                    name: AdPresetPropertyDisplayNames.target_age_groups,
                    key: 'target_age_groups',
                    fields: ['target_age_groups'],
                    checked: false,
                },
                {
                    name: AdPresetPropertyDisplayNames.geoboxes,
                    key: 'geoboxes',
                    fields: ['geoboxes'],
                    checked: false,
                },
            ],
        },
        {
            groupName: getAdPresetGroupDisplayName('Inventory'),
            properties: [
                {
                    name: AdPresetPropertyDisplayNames.target_dooh_publishers,
                    key: 'target_dooh_publishers',
                    fields: ['target_dooh_publishers', 'target_dooh_publishers_exclude'],
                    checked: false,
                },
            ],
        },
        {
            groupName: getAdPresetGroupDisplayName('Bid Optimization'),
            properties: [
                {
                    name: AdPresetPropertyDisplayNames.max_cpm_rate_local,
                    key: 'max_cpm_rate_local',
                    fields: ['max_cpm_rate_local'],
                    checked: false,
                },
            ],
        },
    ];

    return { ...defaults };
}

export function validateAdPresetDraft(draft) {
    const _draft = {
        ...draft,
    };

    const draftValidators_forPayload = getDraftValidators_forPayload();

    const validators = _.pick(draftValidators_forPayload, Object.keys(_draft));
    return validate(_draft, validators);
}

// -----------------------------------------
// Data transformation before sending to api
// -----------------------------------------
import { serialize as _serialize } from './serialize-for-api';
export const serializeForApi = _serialize;
